import React, { useEffect, useState } from 'react';
import {
  Award,
  CheckCircle,
  Lock,
  TrendingUp,
  Shield,
  Trophy,
  Star,
} from 'lucide-react';
import { VendorBadgeProgress, BadgeType } from '../types';
import { vendorApi } from '../services/api';
import { extractApiError } from '../utils/errorUtils';
import VendorBadgeIcon from './VendorBadgeIcon';

interface VendorBadgeListProps {
  vendorId: number;
}

/**
 * Show all available badges with progress tracking (Issue #684, #955)
 * Displays earned and locked badges with progress indicators
 */
export default function VendorBadgeList({ vendorId }: VendorBadgeListProps) {
  const [badgeProgress, setBadgeProgress] = useState<VendorBadgeProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<BadgeType | 'all'>('all');
  const [sortBy, setSortBy] = useState<'earned' | 'type' | 'name'>('earned');

  useEffect(() => {
    const fetchBadgeProgress = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await vendorApi.getVendorBadgeProgress(vendorId);
        setBadgeProgress(data);
      } catch (err: unknown) {
        const apiError = extractApiError(err);

        // 404 means no badge progress yet - treat as empty state, not error
        if (apiError.status === 404) {
          console.log('No badge progress found for vendor - showing empty state');
          setBadgeProgress([]);
        } else {
          console.error('Failed to load badge progress:', apiError);
          setError(apiError.message);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBadgeProgress();
  }, [vendorId]);

  // Filter badges
  const filteredBadges =
    filterType === 'all'
      ? badgeProgress
      : badgeProgress.filter((bp) => bp.badge.badge_type === filterType);

  // Sort badges
  const sortedBadges = [...filteredBadges].sort((a, b) => {
    if (sortBy === 'earned') {
      // Earned first, then by type
      if (a.earned !== b.earned) return a.earned ? -1 : 1;
      return a.badge.display_order - b.badge.display_order;
    } else if (sortBy === 'type') {
      return a.badge.badge_type.localeCompare(b.badge.badge_type);
    } else {
      // name
      return a.badge.name.localeCompare(b.badge.name);
    }
  });

  const earnedCount = badgeProgress.filter((bp) => bp.earned).length;

  // Badge type icons
  const typeIcons: Record<BadgeType, React.ReactNode> = {
    achievement: <TrendingUp className="w-4 h-4" />,
    verification: <Shield className="w-4 h-4" />,
    milestone: <Trophy className="w-4 h-4" />,
    special: <Star className="w-4 h-4" />,
  };

  if (loading) {
    return (
      <div
        className="flex items-center justify-center py-12"
        data-testid="badge-list-loading"
      >
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded"
        data-testid="badge-list-error"
      >
        <p className="font-semibold">Failed to load badges</p>
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vendor-badge-list">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Award className="w-6 h-6 text-secondary" />
            Achievement Badges
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {earnedCount} of {badgeProgress.length} badges earned
          </p>
        </div>
      </div>

      {/* Filters and Sort */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        {/* Filter by type */}
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setFilterType('all')}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              filterType === 'all'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All ({badgeProgress.length})
          </button>
          {(['achievement', 'verification', 'milestone', 'special'] as BadgeType[]).map(
            (type) => {
              const count = badgeProgress.filter(
                (bp) => bp.badge.badge_type === type
              ).length;
              if (count === 0) return null;
              return (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors flex items-center gap-1 ${
                    filterType === type
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {typeIcons[type]}
                  {type.charAt(0).toUpperCase() + type.slice(1)} ({count})
                </button>
              );
            }
          )}
        </div>

        {/* Sort */}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as 'earned' | 'type' | 'name')}
          className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="earned">Sort: Earned First</option>
          <option value="type">Sort: By Type</option>
          <option value="name">Sort: By Name</option>
        </select>
      </div>

      {/* Badge Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {sortedBadges.map((badgeProgress) => {
          const { badge, earned, progress } = badgeProgress;
          const hasProgress = progress && Object.keys(progress).length > 0;

          return (
            <div
              key={badge.id}
              className={`
                p-4 rounded-lg border-2 transition-all
                ${
                  earned
                    ? 'border-teal-200 bg-teal-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }
              `}
              data-testid={`badge-${badge.id}`}
            >
              <div className="flex gap-4">
                {/* Badge Icon */}
                <div className="flex-shrink-0">
                  <VendorBadgeIcon badge={badge} earned={earned} size="lg" showTooltip={false} />
                </div>

                {/* Badge Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      {badge.name}
                      {earned && <CheckCircle className="w-4 h-4 text-secondary" />}
                      {!earned && <Lock className="w-4 h-4 text-gray-400" />}
                    </h3>
                    <span
                      className={`
                      px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap
                      ${
                        badge.badge_type === 'verification'
                          ? 'bg-teal-100 text-primary-dark'
                          : badge.badge_type === 'achievement'
                            ? 'bg-moonbone text-primary-dark'
                            : badge.badge_type === 'milestone'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-yellow-100 text-yellow-700'
                      }
                    `}
                    >
                      {badge.badge_type}
                    </span>
                  </div>

                  <p className="text-sm text-gray-600 mt-1">{badge.description}</p>

                  {/* Progress */}
                  {!earned && hasProgress && (
                    <div className="mt-3 space-y-1">
                      {Object.entries(progress).map(([key, value]) => (
                        <div key={key} className="text-xs text-gray-500">
                          <span className="font-medium capitalize">
                            {key.replace(/_/g, ' ')}:
                          </span>{' '}
                          <span className="text-gray-700">{String(value)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Requirements for locked badges */}
                  {!earned && !hasProgress && badge.requirements && (
                    <div className="mt-2 text-xs text-gray-500">
                      <span className="font-medium">Requirements:</span>{' '}
                      {badge.requirements}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty state */}
      {sortedBadges.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Award className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="text-lg font-medium text-gray-700 mb-2">
            {badgeProgress.length === 0
              ? 'Start Earning Badges!'
              : 'No badges found in this category'}
          </p>
          {badgeProgress.length === 0 && (
            <p className="text-sm text-gray-500 max-w-md mx-auto">
              Complete profile tasks, participate in fairs, and build your reputation to earn
              achievement badges. Check back here to track your progress!
            </p>
          )}
        </div>
      )}
    </div>
  );
}
